import { Capacitor } from '@capacitor/core';
import { ADMOB_CONFIG, ADS_ENABLED, IS_DEBUG_BUILD, TEST_DEVICE_IDS } from '../config/admob';
import { auth } from '../firebase';

let AdMobModule = null;
let isInitialized = false;

/**
 * Checks if the user is running the official native Android application.
 */
export const isAndroidNative = () => {
  if (typeof window !== 'undefined' && window.__TEST_ANDROID_MODE__) {
    return true; // Optional testing bypass in local dev
  }
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
};

/**
 * Dynamically loads the Capacitor AdMob plugin to prevent web compilation/runtime errors.
 */
export async function loadAdMob() {
  if (!ADS_ENABLED) return null;
  if (AdMobModule) return AdMobModule;
  try {
    const mod = await import('@capacitor-community/admob');
    AdMobModule = mod;
    return AdMobModule;
  } catch (err) {
    console.warn('[AdMobManager] Unable to load @capacitor-community/admob:', err);
    return null;
  }
}

/**
 * Initializes AdMob natively once per application lifecycle.
 */
export async function initAdMob() {
  if (!ADS_ENABLED) return false;
  if (!isAndroidNative()) return false;
  if (isInitialized) return true;

  const admob = await loadAdMob();
  if (!admob) return false;

  try {
    // Google needs consent from players in the EEA, UK and some other regions before any ad request.
    // A failure here means no ads rather than ads without consent.
    let consent = await admob.AdMob.requestConsentInfo();
    if (!consent.canRequestAds && consent.isConsentFormAvailable) {
      consent = await admob.AdMob.showConsentForm();
    }
    if (!consent.canRequestAds) {
      console.warn('[AdMobManager] Consent status does not allow ads.');
      return false;
    }

    await admob.AdMob.initialize({
      initializeForTesting: IS_DEBUG_BUILD || TEST_DEVICE_IDS.length > 0,
      testingDevices: TEST_DEVICE_IDS
    });
    isInitialized = true;
    console.log('[AdMobManager] Initialized successfully on Android');
    return true;
  } catch (e) {
    console.error('[AdMobManager] Initialization error:', e);
    return false;
  }
}

/**
 * Prepares and displays a rewarded video ad.
 * Resolves with { rewarded: true } upon completion.
 * Rejects if dismissed early, failed to load, or not supported.
 *
 * @param {string} placementKey - e.g. 'dungeonSpeedup', 'campfireRest', 'dungeonDoubler', 'dragonShrine', 'wheelSpin'
 */
export async function showRewardedAd(placementKey) {
  if (!ADS_ENABLED) throw new Error('Video ads are turned off.');
  if (!isAndroidNative()) {
    // If in debug/test mode on web
    if (typeof window !== 'undefined' && window.__TEST_ANDROID_MODE__) {
      console.log(`[AdMobManager] TEST MODE: Simulating rewarded ad for ${placementKey}`);
      await new Promise(r => setTimeout(r, 1200));
      return { rewarded: true, test: true };
    }
    throw new Error('Rewarded ads are exclusive to the WHATIF Battle For Riches Android App.');
  }

  const admob = await loadAdMob();
  if (!admob) {
    throw new Error('AdMob plugin could not be loaded on this device.');
  }

  // The reward goes to this account, so a video can't start without one
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error('Please log in first.');

  if (!(await initAdMob())) {
    throw new Error("Videos aren't available right now. Please try again later.");
  }

  const adId = ADMOB_CONFIG.getAdUnitId(placementKey);
  const { AdMob, RewardAdPluginEvents } = admob;

  return new Promise(async (resolve, reject) => {
    let rewarded = false;
    let rewardData = null;
    let listeners = [];

    const cleanup = () => {
      listeners.forEach(handle => {
        try {
          if (handle && handle.remove) handle.remove();
        } catch (e) {}
      });
      listeners = [];
    };

    try {
      const rewardedListener = await AdMob.addListener(RewardAdPluginEvents.Rewarded, (reward) => {
        console.log(`[AdMobManager] User rewarded for ${placementKey}:`, reward);
        rewarded = true;
        rewardData = reward;
      });
      listeners.push(rewardedListener);

      const dismissedListener = await AdMob.addListener(RewardAdPluginEvents.Dismissed, () => {
        cleanup();
        if (rewarded) {
          resolve({ rewarded: true, data: rewardData });
        } else {
          reject(new Error('Ad video was closed before completion. Reward not granted.'));
        }
      });
      listeners.push(dismissedListener);

      const failedListener = await AdMob.addListener(RewardAdPluginEvents.FailedToLoad, (err) => {
        cleanup();
        console.warn(`[AdMobManager] Failed to load ad for ${placementKey}:`, err);
        reject(new Error(`Failed to load video ad stream: ${err?.message || 'Network error'}`));
      });
      listeners.push(failedListener);

      console.log(`[AdMobManager] Preparing reward video ad [${placementKey}] id: ${adId}`);
      await AdMob.prepareRewardVideoAd({
        adId: adId,
        clearOnHide: true,
        ssv: { customData: uid }
      });

      console.log(`[AdMobManager] Showing reward video ad [${placementKey}]`);
      await AdMob.showRewardVideoAd();
    } catch (error) {
      cleanup();
      console.error(`[AdMobManager] Error playing rewarded ad for ${placementKey}:`, error);
      reject(error);
    }
  });
}


/**
 * Claims a reward after a video. Google's confirmation of a finished video can land a few
 * seconds after the video closes, so a claim that gets there first waits and tries again.
 */
export async function claimAfterAd(claim, data) {
  for (let attempt = 0; ; attempt++) {
    try {
      return await claim(data);
    } catch (err) {
      if (attempt >= 5 || !String(err?.message).includes('still being confirmed')) throw err;
      await new Promise((r) => setTimeout(r, 2000));
    }
  }
}
